import { Controller } from "@hotwired/stimulus"
import { measure } from "./layout_measurement"

// A page's own title-and-action-button header row (an <h1> on the left, a New X button on the
// right, in a plain d-flex justify-content-between align-items-center row) stacks -- title on
// its own line, the action button on a new line below it, filling the row's width -- the moment
// the title would actually wrap onto more than one line. Shortening the button's own label first
// shipped as the fix for this (short_label_controller.js), but that was solving the wrong half
// of the problem: a small button vertically centered next to a title that's wrapped onto two
// lines looks exactly as awkward as that sounds regardless of how short the button's own text
// manages to get, and a real record-creation button ("New notification rule") reads better kept
// whole. Stacking instead of shortening is the actual fix; the button keeps its full label always.
//
// Checked by real measurement (layout_measurement.js, batched across every row like this one on
// the page at once): does the title's own rendered height exceed a single line's, i.e. did it
// actually wrap, not a guessed screen-size breakpoint. Two pages with the same title length can
// still wrap differently depending on how long the button's own label is or how much else is in
// the row, so nothing about the *screen* width alone reliably predicts it either way.
export default class extends Controller {
  static targets = ["title"]

  connect() {
    this.update = this.update.bind(this)
    this.update()
    window.addEventListener("resize", this.update)
  }

  disconnect() {
    window.removeEventListener("resize", this.update)
  }

  update() {
    // Reset to side-by-side before (re-)measuring: stacking already gives the title the full row
    // width, which can make a previously-wrapped title fit on one line at the very width that
    // caused the wrap in the first place, a false "still wrapped" reading if left stacked.
    this.element.classList.remove("header-actions-stacked")

    measure(this.titleTarget, (el) => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight)
      return el.scrollHeight > lineHeight * 1.5
    }, (wrapped) => {
      this.element.classList.toggle("header-actions-stacked", wrapped)
    })
  }
}
